/**
  * @class mmodLogin
  * @description
  * Login module provides user ability to authenticate to Mosy system.
  * It is used in both, mOffice and mPatrol the same way.
  * Login module is implemented without workflow, as a pure module named mmodLogin.
  * It does not send login data to server directly, but rather sends mmUserLogin
  * to msysUser which then resends it to msvrUsers server service.
  * Login module listens to mmUserLogged message coming from msysUser to hide itself
  * or display info on unsuccessful login credentials.
  */

import mmodModule from 'mmodModule'
const mfs = require('msysFS')

export default class mmodLogin extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: { type: 'float', order: 3, x: 0, y: 0, width: '100%', height: '100%' },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      login_format: {
        type: 'string',
        description: 'formatting of login',
        default: '^\\w+$',
      },
      login_transform: {
        type: 'string',
        descriptions: 'case transform of login; empty to disable transform.',
        default: 'lowercase',
      },
      callsign_format: {
        type: 'string',
        description: 'formatting of callsign; empty to disable field completely, value "login" will cause callsign to equal to login',
        default: '^\\S*$',
      },
      callsign_transform: {
        type: 'string',
        descriptions: 'case transform of login; empty to disable transform.',
        default: 'uppercase',
      },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.loadForm(require('./mmodLogin.json'))

    // setup appinfo field
    this.el.forAll('@appinfo', el=>{
      el.innerHTML = String(this.app_id??'')+' : '+MOSY_APP_WITH_VERSION
    })

    // setup login field
    const login_format = String(this.config.login_format ?? '')
    const login_transform = String(this.config.login_transform ?? '')
    const login_value = String(mfs.getItem('last_login') ?? '')
    for(const el of this.el.findAll('@login')) {
      el.format = login_format
      el.transform = login_transform
      el.value = login_value
    }

    // setup callsign field
    const callsign_format = String(this.config.callsign_format ?? '')
    const callsign_transform = String(this.config.callsign_transform ?? '')
    const callsign_value = String(mfs.getItem('last_callsign') ?? '')
    for(const el of this.el.findAll('@callsign')) {
      if(callsign_format && callsign_format!=='login') {
        el.format = callsign_format
        el.transform = callsign_transform
        el.value = callsign_value
      }else{
        el.hidden = true
      }
    }

    this.mmStart()
  } // initWindow

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that it successfully identifies with given app_id
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} [params.app_key] - private key of application
  * @param {string} [params.app_cert] - application certificate signed by deployment PKI CA
  * @param {string} [params.unit_id] - unique identifier for the logical patrolling unit
  */
  mmAppIdentified(sender, params) {
    super.mmAppIdentified(sender, params)
    this.el.forAll('@appinfo', el=>{
      el.innerHTML = _.toString(this.app_id)+' : '+MOSY_APP_WITH_VERSION
    })
  } // mmAppIdentified

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sent by &lt;mele-container> or derived class to parent module when form is submitted
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.holder - key attribute of the form
  * @param {string} [params.button] - key attribute of the button
  * @param {string} [params.datakey] - key attribute of the content
  * @param {object} params.values - hash of key:value of contained editable inputs
  * @param {boolean} [params.invalid] - true if any of form's inputs is invalid
  * @param {boolean} [params.conflict] - true if any of form's inputs has conflict
  */
  mmFormSubmit(sender, params) {
    if(params.holder==='loginform') {
      if(this.config.callsign_format==='login') {
        params.values.callsign = _.toString(params.values.login)
      }
      this.el.find('@password').value=''
      this.el.find('@error').label = ''
      mfs.setItem('last_login', _.toString(params.values.login))
      mfs.setItem('last_callsign', _.toString(params.values.callsign))
      this.publish('msysUser', 'mmUserLogin', params.values)
      this.el.views = 'logininprogress'
    }else if(params.holder==='loginproblem') {
      if(/^wss?:\/\/[^:/]+(:\d+)?\/?$/.test(params.values.server)) {
        this.publish('msysDomainClient', 'mmChangeServers', { servers: [params.values.server] })
      }
    }
  } // mmFormSubmit

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sent by &lt;mele-container> or derived class to parent module when form is cancelled
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.holder - key attribute of the form
  * @param {string} [params.datakey] - key attribute of the content
  */
  mmFormCancel(sender, params) {
    if(params.holder==='loginproblem') {
      this.el.views = 'loginwait'
    }
  } // mmFormCancel

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on changed user authorization; user_id is empty if not logged
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {integer} [params.user_id] - user's unique ID
  * @param {string} [params.login] - user's login name
  * @param {string} [params.callsign] - user's callsign
  * @param {object} [params.roles] - roles assigned to user
  * @param {string} [params.privatekey] - user's private key encrypted by his password
  * @param {string} [params.error] - error message if login failed
  */
  mmUserLogged(sender, params) {
    const previous_user_id = this.user_id
    super.mmUserLogged(sender, params)
    if(params.user_id) {
      this.el.find('@error').label = ''
      this.publish('msysWM', 'mmWindowSet', { type: 'none' })
      mfs.setItem('last_login', _.toString(params.login))
      mfs.setItem('last_callsign', _.toString(params.callsign))
      setTimeout(()=>{ this.el.views = 'loginform' }, 500)
      if(previous_user_id != this.user_id) {
        const hash = window.location.hash
        if(hash) {
          this.el.ownerDocument.location.hash = ''
          setTimeout(()=>{ this.el.ownerDocument.location.hash = hash }, 100)
        }
      // this.modalWait('wf.generic.LoadingObject')
      // setTimeout(()=>this.modalCloseDefault(), MOSY_APP=='mOffice' ? 2000 : 2000)
      }
    }else{
      let error_label = ''
      if(this.el.views === 'logininprogress') {
        // determine error message based on server response
        error_label = params.error || 'login.errorlogin'
      }
      this.el.find('@error').label = error_label
      this.el.views = 'loginform'
      this.publish('msysWM', 'mmWindowSet', this.config.init)
      const elLogin = this.el.find('@login')
      if(elLogin && elLogin.value.length>0) {
        setTimeout(()=>{ this.el.find('@password input').focus() }, 100)
      }
    }
  } // mmUserLogged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Automatically invoked when all expected listeners were started or exited
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {integer} [params.init] - timestamp when application starts with loading listeners
  * @param {integer} [params.loaded] - timestamp when all listeners are loaded and first listener starts
  * @param {integer} [params.started] - timestamp when all listeners started
  */
  mmStartedAll() {
    setTimeout(()=>{ this.el.find('@password input').focus() }, 100)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer is established
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - app_id of the server
  * @param {string} params.url - URL of connected server
  * @param {string} [params.ident] - Identity string of server
  * @param {string} [params.client_ip] - IP of the client as recognized by the server
  */
  mmServerConnected(sender, params) {
    this.el.views = 'loginform'
    setTimeout(()=>{ this.el.find('@password input').focus() }, 300)
  } // mmServerConnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that connection to mServer has dropped
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.reason] - reason for close
  * @param {string} params.url - URL of disconnected server
  */
  mmServerDisconnected(sender, params) {
    // we will implement token's expiration here
    // for now, we will not show login on disconnect

    // this.el.views = 'loginwait'
  } // mmServerDisconnected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  * @param {integer} [params.count] - number of times button was clicked
  * @param {boolean} [params.long] - shows if press was long or not
  */
  mmButtonClicked(sender, params) {
    if(params.button==='problem') {
      this.el.views = 'loginproblem'
    }
  } // mmButtonClicked
} // mmodLogin
